import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import findFirstMatchingNodeAlongEveryPath from 'util/graph-utils/findFirstMatchingNodeAlongEveryPath';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';
import {assertNever} from 'util/Types';

import getSchemaMapperConfigsMappingToNode from '../util/getSchemaMapperConfigsMappingToNode';
import {
  ProcessEdgeForValidation,
  ProcessNodeForValidation,
  ProcessTaskSchemaNodeForValidation,
  ProcessFlowchartForValidation,
} from './ProcessFlowchartForValidationTypes';
import {
  ProcessGraphBuilderNodeTemplate,
  SchemaMapperConfig,
  SchemaMapperConfigWithMapperData,
} from './ProcessGraphBuilderConstants';

export function getTemplateNodeFromClientId(
  nodes: Array<ProcessGraphBuilderNodeTemplate>,
  clientId: string
): ProcessGraphBuilderNodeTemplate | null {
  return nodes.find((tNode) => tNode.clientId === clientId) || null;
}

export function getTemplateNodeFromSchemaId(
  nodes: Array<ProcessGraphBuilderNodeTemplate>,
  schemaId: string
): ProcessGraphBuilderNodeTemplate | null {
  return nodes.find((tNode) => tNode.schemaId === schemaId) || null;
}

export function getSchemaIdFromNodeClientId(
  flowchart: ProcessFlowchartForValidation,
  clientId: string
): string | null {
  const node = getFlowchartNodeFromClientId(flowchart, clientId);
  if (node == null) {
    return null;
  }

  switch (node.nodeType) {
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return node.taskSchemaId;
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      return null;
    default:
      assert(false, `Invalid node type ${assertNever(node)}`);
      return null;
  }
}

export function getNodesByClientId(
  flowchart: ProcessFlowchartForValidation
): Record<string, ProcessNodeForValidation> {
  const nodeById: Record<string, ProcessNodeForValidation> = {};
  for (const node of flowchart.nodes) {
    nodeById[node.id] = node;
  }
  return nodeById;
}

export function getAdjacencyMapFromProcessFlowchart(flowchart: ProcessFlowchartForValidation): AdjacencyMap {
  const adjacencyMap: AdjacencyMap = {};
  for (const node of flowchart.nodes) {
    adjacencyMap[node.id] = [];
  }
  for (const edge of extractControlFlowEdges(flowchart.edges)) {
    if (adjacencyMap[edge.source] == null) {
      adjacencyMap[edge.source] = [];
    }
    adjacencyMap[edge.source].push(edge.target);
    if (adjacencyMap[edge.target] == null) {
      adjacencyMap[edge.target] = [];
    }
  }
  return adjacencyMap;
}

export function extractControlFlowEdges(
  edges: Array<ProcessEdgeForValidation>
): Array<ProcessEdgeForValidation> {
  return edges.filter((edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW);
}

/**
 * Gets the closest downstream task nodes, accounting for chained routers.
 * Returns multiple task nodes when node branches into multiple task nodes or leads to a router node that eventually branches into multiple task nodes.
 */
export function getTaskNodesDownstreamOfNode(
  nodeClientId: string,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessTaskSchemaNodeForValidation> {
  const adjacencyMap: AdjacencyMap = {};
  const taskNodeById: Record<string, ProcessTaskSchemaNodeForValidation> = {};
  for (const node of flowchart.nodes) {
    adjacencyMap[node.id] = [];
    if (
      node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    ) {
      taskNodeById[node.id] = node;
    }
  }
  for (const edge of extractControlFlowEdges(flowchart.edges)) {
    if (adjacencyMap[edge.source] == null) {
      adjacencyMap[edge.source] = [];
    }
    adjacencyMap[edge.source].push(edge.target);
  }

  return findFirstMatchingNodeAlongEveryPath(nodeClientId, adjacencyMap, (nodeId) =>
    Object.keys(taskNodeById).includes(nodeId)
  ).map(({nodeId}) => taskNodeById[nodeId]);
}

/**
 * Gets the most recent task node ancestors of a node, accounting for multiple incoming edges
 * and chained routers.
 */
export function getTaskNodesUpstreamOfNode(
  nodeClientId: string,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessTaskSchemaNodeForValidation> {
  const invertedAdjacencyMap: AdjacencyMap = {};
  const taskNodeById: Record<string, ProcessTaskSchemaNodeForValidation> = {};
  for (const node of flowchart.nodes) {
    invertedAdjacencyMap[node.id] = [];
    if (
      node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) {
      taskNodeById[node.id] = node;
    }
  }
  for (const edge of extractControlFlowEdges(flowchart.edges)) {
    if (invertedAdjacencyMap[edge.target] == null) {
      invertedAdjacencyMap[edge.target] = [];
    }
    invertedAdjacencyMap[edge.target].push(edge.source);
  }

  return findFirstMatchingNodeAlongEveryPath(nodeClientId, invertedAdjacencyMap, (nodeId) =>
    Object.keys(taskNodeById).includes(nodeId)
  ).map(({nodeId}) => taskNodeById[nodeId]);
}

export function getSchemaIdMappingToNode(
  node: Omit<ProcessTaskSchemaNodeForValidation, 'taskDefinitionVersionSpec' | 'executionType'>,
  upstreamNode: Omit<ProcessTaskSchemaNodeForValidation, 'taskDefinitionVersionSpec' | 'executionType'>,
  schemaMapperConfigs: Array<SchemaMapperConfigWithMapperData>,
  mapperConfigByClientId: {[clientId: string]: SchemaMapperConfigWithMapperData}
): string | null {
  const mapperConfigsForNode = getSchemaMapperConfigsMappingToNode(
    {taskSchemaId: node.taskSchemaId},
    new Set([upstreamNode.taskSchemaId]),
    schemaMapperConfigs.map(({clientId, fromTaskSchemas, toTaskSchema}) => ({
      clientId,
      fromTaskSchemaIds: fromTaskSchemas.map(({id}) => id),
      toTaskSchemaId: toTaskSchema.id,
    }))
  );
  if (mapperConfigsForNode?.length !== 1) {
    return null;
  }

  const fromSchemaIds = mapperConfigByClientId[
    mapperConfigsForNode[0].clientId
  ].mapperConfig.schemaFieldMapperConfigs.map(({fromSchemaId}) => fromSchemaId);
  if (new Set(fromSchemaIds).size !== 1) {
    return null;
  }
  return fromSchemaIds[0];
}

export function getFlowchartNodeFromClientId(
  flowchart: ProcessFlowchartForValidation,
  clientId: string
): ProcessNodeForValidation | undefined {
  return flowchart.nodes.find((node) => node.id === clientId);
}

export function getInboundEdges(
  node: ProcessNodeForValidation,
  edges: Array<ProcessEdgeForValidation>
): Array<ProcessEdgeForValidation> {
  return edges.filter(({target}) => target === node.id);
}

export function getOutboundEdges(
  node: ProcessNodeForValidation,
  edges: Array<ProcessEdgeForValidation>
): Array<ProcessEdgeForValidation> {
  return edges.filter(({source}) => source === node.id);
}

export function getEdgeDisplayName(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): string {
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);

  return fromNode == null || toNode == null ? edge.id : `${fromNode.name} - ${toNode.name}`;
}

export function getFulfillingMapperConfigs(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  templateNode: ProcessGraphBuilderNodeTemplate
): Array<SchemaMapperConfig> | null {
  // Only task and output nodes can be mapped to. All other node types cannot have any mappers configured so we
  // return null.
  if (
    node.nodeType !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    node.nodeType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  ) {
    return null;
  }

  const inboundSchemas = new Set(
    compact(
      getTaskNodesUpstreamOfNode(node.id, flowchart).map((upstreamNode) =>
        getSchemaIdFromNodeClientId(flowchart, upstreamNode.id)
      )
    )
  );

  return getSchemaMapperConfigsMappingToNode(
    {taskSchemaId: node.taskSchemaId},
    inboundSchemas,
    templateNode.inboundMapperConfigs
  );
}
